import { CardSubWrapper, CardWrapper } from 'admin/components/wrappers';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import { CustomDeletePopup } from 'app/components/CustomDeletePopup';
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigateToRoute } from 'utils/common';
import ShowNameCell from 'app/components/CustomDataGrid/Cells/ShowNameCell';
import ShowDateCell from 'app/components/CustomDataGrid/Cells/ShowDateCell';
import ShowAddressCell from 'app/components/CustomDataGrid/Cells/ShowAddressCell';
import ShowHostCell from 'app/components/CustomDataGrid/Cells/ShowHostCell';
import ShowLocationCell from 'app/components/CustomDataGrid/Cells/ShowLocationCell';
import ShowStatusCell from 'app/components/CustomDataGrid/Cells/ShowStatusCell';
import { GeneralLookup, ShowWrapper } from 'services/openapi';
import Cookies from 'js-cookie';
import { useLocation } from 'react-router-dom';
import { SortDescriptor } from '@progress/kendo-data-query';
import {
  GridColumnProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import HorizontalKebabCell from 'app/components/CustomDataGrid/Cells/HorizontalKebabCell';
import { usePermission } from 'hooks/usePermission';

type Props = {
  setPageSkip: React.Dispatch<React.SetStateAction<number | undefined>>;
  pageSkip: number | undefined;
  pageSize: number;
  debounceShowSearch: string;
  fetchManageGetShows: (
    season?: string | undefined,
    location?: string[] | undefined,
    categoryFilter?: string[] | undefined,
    divisionFilter?: string[] | undefined,
    status?: string | undefined,
    searchInput?: string | undefined,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  showData?: ShowWrapper;
  selectedItemId: string | null;
  deleteShow: (
    id?: string | undefined,
    season?: string | undefined,
    location?: string[] | undefined,
    categoryFilter?: string[] | undefined,
    divisionFilter?: string[] | undefined,
    status?: string | undefined,
    searchInput?: string | undefined,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  fetchShowActiveOrInactive: (
    id: string,
    activeStatusId?: string,
    item?: string,
    season?: string,
    location?: Array<string>,
    categoryFilter?: Array<string>,
    divisionFilter?: Array<string>,
    status?: string,
    searchInput?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  lookup: GeneralLookup[];
  sortField: string | undefined;
  sortOrder: number | undefined;
  setSortField: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSortOrder: React.Dispatch<React.SetStateAction<number | undefined>>;
  setIsOrder: React.Dispatch<React.SetStateAction<boolean>>;
  isOrder: boolean;
  loading: boolean;
  handleShowDetailsDwoload: (showId?: string) => Promise<any>;
  pageSizeSelected: GeneralLookup;
  categoryFilter?: Array<string>;
  divisionFilter?: Array<string>;
};
const CurrentSeasonTable = ({
  setPageSkip,
  pageSkip,
  pageSize,
  debounceShowSearch,
  fetchManageGetShows,
  showData,
  selectedItemId,
  deleteShow,
  fetchShowActiveOrInactive,
  lookup,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  setIsOrder,
  isOrder,
  loading,
  handleShowDetailsDwoload,
  pageSizeSelected,
  categoryFilter = [],
  divisionFilter = [],
}: Props) => {
  const redirect = useNavigateToRoute();
  const currentLocation = useLocation();
  const { hasViewPermission } = usePermission();
  const isPastDataFlag = currentLocation.pathname.includes('past-data');
  const [showDeletePopup, setShowDeletePopup] = React.useState<boolean>(false);
  const activeId = lookup.find(item => item.name === 'Active');
  const inActiveId = lookup.find(item => item.name === 'Inactive');
  const [showId, setShowId] = useState('');
  const userType = Cookies.get('userType') ?? '';
  const initialSort: Array<SortDescriptor> = useMemo(
    () => [{ field: 'showName', dir: 'asc' }],
    [],
  );
  const [sort, setSort] = React.useState(initialSort);
  React.useEffect(() => {
    setSort(initialSort);
  }, [pageSize, initialSort]);
  const handleMoreClick = (item: string, e) => {
    const id = e.dataItem.id;
    if (item === 'Delete show') {
      if (hasViewPermission('0500101015')) {
        setShowId(e.dataItem.id);
        setShowDeletePopup(true);
      }
    } else if (item === 'Make active') {
      if (hasViewPermission('0500101006')) {
        fetchShowActiveOrInactive(
          id,
          activeId?.id,
          item,
          '',
          [],
          categoryFilter,
          divisionFilter,
          selectedItemId ?? '',
          debounceShowSearch,
          sortField,
          sortOrder,
          pageSkip,
          pageSize,
        );
      }
    } else if (item === 'Make inactive') {
      if (hasViewPermission('0500101006')) {
        fetchShowActiveOrInactive(
          id,
          inActiveId?.id,
          item,
          '',
          [],
          categoryFilter,
          divisionFilter,
          selectedItemId ?? '',
          debounceShowSearch,
          sortField,
          sortOrder,
          pageSkip,
          pageSize,
        );
      }
    } else if (item === 'Edit Showinfo') {
      if (hasViewPermission('0500101005')) {
        redirect(
          userType === 'COACH'
            ? `/coach/shows/edit/${id}`
            : `/admin/shows/edit/${id}`,
        );
      }
    } else if (item === 'Duplicate Show') {
      if (hasViewPermission('0500101007')) {
        Cookies.set('showId', id);
        redirect(
          userType === 'COACH'
            ? `/coach/shows/duplicate`
            : `/admin/shows/duplicate`,
        );
      }
    } else if (item === 'Generate Split') {
      if (hasViewPermission('0500101008')) {
        redirect(
          userType === 'COACH'
            ? `/coach/shows/generate-split/${id}`
            : `/admin/shows/generate-split/${id}`,
        );
      }
    } else if (item === 'Enter Results') {
      if (hasViewPermission('0500101010')) {
        redirect(
          userType === 'COACH'
            ? `/coach/shows/show-result/show-status/${id}/${e.dataItem.showName}`
            : `/admin/shows/show-result/show-status/${id}/${e.dataItem.showName}`,
        );
      }
    } else if (item === 'Class Order Reports') {
      if (hasViewPermission('0700101011')) {
        redirect(`/admin/reports/show-rider-report-groups/${id}`);
      }
    } else if (item === 'Show Details') {
      if (hasViewPermission('0700101012')) {
        redirect(`/admin/reports/show-details/${id}`);
      }
    } else if (item === 'Download Show Program') {
      handleShowDetailsDwoload(id);
    }
  };
  const handleViewClick = e => {
    if (isPastDataFlag) {
      redirect(`/admin/past-data/shows/view/show-info/${e.id}`);
    } else {
      if (hasViewPermission('0500101003')) {
        redirect(
          userType === 'COACH'
            ? `/coach/shows/view/${e.id}/show-info`
            : `/admin/shows/view/${e.id}/show-info`,
        ); //pass dynamic id from table
      }
    }
  };
  const baseColumns: GridColumnProps[] = [
    {
      field: 'showName',
      title: 'Show Name',
      cell: e => <ShowNameCell e={e} onViewClick={e => handleViewClick(e)} />,
      width: '250',
      locked: true,
      sortable: true,
      className: 'show-name-style',
      headerClassName: 'show-header-style',
    },
    {
      field: 'showDate',
      title: 'Show Date',
      cell: e => <ShowDateCell e={e} />,
      width: '125',
      sortable: true,
      headerClassName: 'show-date-header-style',
    },

    {
      field: 'showAddress',
      title: 'Show Address',
      cell: e => <ShowAddressCell e={e} />,
      width: '180',
      sortable: false,
    },
    {
      field: 'showLocation',
      title: 'Show Location',
      cell: e => <ShowLocationCell e={e} />,
      width: '125',
      sortable: false,
    },
    {
      field: 'hostedBy',
      title: 'Hosted By',
      cell: e => <ShowHostCell e={e} />,
      width: '150',
      sortable: false,
    },
    {
      field: 'status',
      title: 'Status',
      cell: e => <ShowStatusCell e={e} />,
      width: '100',
      sortable: false,
    },
  ];
  const actionColumn: GridColumnProps[] =
    userType !== 'COACH'
      ? [
          {
            field: 'action',
            title: 'Action',
            cell: e => (
              <HorizontalKebabCell
                isPastDataFlag={isPastDataFlag}
                isAssigned={true}
                e={e}
                onMoreClick={handleMoreClick}
                isCurrentSeason={true}
              />
            ),
            width: `${isPastDataFlag ? '0' : '100'}`,
            sortable: false,
            locked: true,
          },
        ]
      : [];
  const ShowColumn: GridColumnProps[] = actionColumn.concat(baseColumns);
  const ShowPastDataColumn = ShowColumn.slice(0, -1);
  const handlePageChange = event => {
    setPageSkip(event.page.skip);
    fetchManageGetShows(
      '',
      [],
      categoryFilter,
      divisionFilter,
      selectedItemId ?? '',
      debounceShowSearch,
      sortField,
      sortOrder,
      event.page.skip,
      pageSize,
    );
  };

  const handleCurrentSeasonShow = () => {
    const currentItemCount = showData?.shows?.length ?? 0;

    // Calculate next page skip if we're deleting the last item on this page
    let nextPageSkip = pageSkip;
    if (currentItemCount <= 1 && pageSkip && pageSkip > 0) {
      nextPageSkip = pageSkip - pageSize;
      setPageSkip(nextPageSkip);
    }

    setShowDeletePopup(false);
    deleteShow(
      showId,
      '',
      [],
      categoryFilter,
      divisionFilter,
      selectedItemId ?? '',
      debounceShowSearch,
      sortField,
      sortOrder,
      nextPageSkip ?? 0,
      pageSize,
    );
  };
  const handleSortChange = e => {
    let sortOrderValue = e.dir === 'asc' ? 0 : 1;
    setSortOrder(sortOrderValue);
    setSortField(e.field);
    fetchManageGetShows(
      '',
      [],
      categoryFilter,
      divisionFilter,
      selectedItemId ?? '',
      debounceShowSearch,
      e.field,
      sortOrderValue,
      pageSkip,
      pageSize,
    );
  };
  return (
    <>
      {loading ? (
        <TableSkeletonLoader rowContent={8} width={100} headingWidth={80} />
      ) : (
        <CardWrapper style={{ paddingTop: '15px' }}>
          <CardSubWrapper className="d-flex"></CardSubWrapper>
          <GridWrapper>
            <CustomDataGrid
              showPagination={
                (showData?.pageDetails?.count ?? 0) >
                Number(pageSizeSelected.id)
              }
              data={showData?.shows ?? []}
              columns={isPastDataFlag ? ShowPastDataColumn : ShowColumn}
              pageSize={pageSize}
              totalCount={showData?.pageDetails?.count}
              onPageChange={event => {
                handlePageChange(event);
              }}
              skip={pageSkip}
              sortable={{
                allowUnsort: false,
                mode: 'single',
              }}
              sort={sort}
              onSortChange={(e: GridSortChangeEvent) => {
                let sortvalue = e.sort[0];
                setSort(e.sort);
                handleSortChange(sortvalue);
              }}
            />
            <CustomDeletePopup
              deleteIcon={true}
              headerUnderline={false}
              popupSubLabel="Are you sure you want to delete this show?"
              className="body-scroll-lock height-full delete-update"
              popupLabel="Delete show"
              setVisible={setShowDeletePopup}
              visible={showDeletePopup}
              align="centre"
              btnLabel1="Cancel"
              btnLabel2="Delete"
              handlebtn2Click={handleCurrentSeasonShow}
              handlebtn1Click={() => {
                setShowDeletePopup(false);
              }}
            />
          </GridWrapper>
        </CardWrapper>
      )}
    </>
  );
};

export default CurrentSeasonTable;
const GridWrapper = styled.div`
  .k-grid-container {
    height: calc(100vh - 250px);
  }
  .k-grid-content tr {
    &:hover {
      td {
        background: #f0f0f0 !important;
      }
    }
  }
  .k-grid-content tr {
    td {
      &.k-grid-content-sticky {
        cursor: default;
      }
    }
    .show-name-style {
      cursor: pointer !important;
      .name-label {
        cursor: pointer !important;
      }
    }
  }
  .k-table-thead {
    .k-link {
      cursor: default;
    }
    .show-header-style,
    .show-date-header-style {
      cursor: pointer !important;
      .k-link {
        cursor: pointer !important;
      }
    }
  }
`;
